import { SDK_ERRORS } from '@riz/contracts/sdk';
import { rejectWith } from '../core/errors.ts';
import type { SdkClient } from '../core/client.ts';

export interface RoomState {
  roomId: string;
  joinable: boolean;
  maxPlayers?: number;
  params?: Record<string, unknown>;
}

export interface SocialModule {
  readonly isInstantMultiplayer: boolean;
  inviteLink(params?: Record<string, unknown>): Promise<string>;
  getInviteParam(key: string): string | null;
  updateRoom(room: RoomState): Promise<void>;
  leftRoom(): Promise<void>;
  onJoinRoom(handler: (detail: { roomId: string; params: Record<string, unknown> }) => void): () => void;
  share(content: { title?: string; text?: string; url?: string }): Promise<boolean>;
}

/**
 * Riz owns the invite URL shape and the room registry so the platform can offer
 * join buttons and friend invites. It does not carry gameplay traffic — that
 * stays on the creator's own infrastructure, and a game that assumes otherwise
 * will not work.
 */
export function createSocialModule(client: SdkClient): SocialModule {
  return {
    get isInstantMultiplayer() {
      return readInviteParam('rizInstant') === 'true';
    },

    async inviteLink(params = {}) {
      const capability = client.requireCapability<string>('social.invites', 'Invites');
      if (capability) return capability;
      const result = await client.call('command', 'social/invite', { params });
      if (typeof result.url !== 'string') {
        return rejectWith(SDK_ERRORS.serverError, 'Riz did not return an invite link.');
      }
      void client.track('invite_created', { params }).catch(() => {});
      return result.url;
    },

    getInviteParam(key) {
      return readInviteParam(key);
    },

    async updateRoom(room) {
      const capability = client.requireCapability<void>('social.rooms', 'Multiplayer rooms');
      if (capability) return capability;
      if (!room?.roomId) {
        return rejectWith(SDK_ERRORS.invalidPayload, 'updateRoom needs a roomId.');
      }
      await client.call('command', 'social/room', {
        roomId: room.roomId,
        joinable: room.joinable !== false,
        maxPlayers: room.maxPlayers ?? null,
        params: room.params ?? {},
      });
      void client.track('room_updated', { roomId: room.roomId, joinable: room.joinable }).catch(() => {});
    },

    async leftRoom() {
      if (!client.capabilities.has('social.rooms')) return;
      await client.call('command', 'social/room', { left: true }).catch(() => {});
      void client.track('room_left', {}).catch(() => {});
    },

    /**
     * Fires when a player follows an invite while the game is already running.
     * Without it a game only ever sees invites at load, which is why joining a
     * friend mid-session is the case that most often breaks.
     */
    onJoinRoom(handler) {
      return client.emitter.on<{ roomId: string; params: Record<string, unknown> }>('room-join', (detail) => {
        void client.track('invite_followed', { roomId: detail?.roomId }).catch(() => {});
        handler(detail);
      });
    },

    async share(content) {
      const capability = client.requireCapability<boolean>('social.share', 'Sharing');
      if (capability) return capability;
      const result = await client.call('command', 'social/share', { ...content });
      const shared = result.shared === true;
      if (shared) void client.track('content_shared', {}).catch(() => {});
      return shared;
    },
  };
}

function readInviteParam(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get(key);
  } catch {
    return null;
  }
}
